/**
 * "Вероятность продажи" card — probability bar, key indicators, confidence.
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  AnalysisSection,
  confidenceLevelLabel,
  scoreTextColor,
  scoreBgColor,
  type XMLAnalysisResult,
} from './analysis-utils';

interface SaleProbabilitySectionProps {
  result: XMLAnalysisResult;
}

export function SaleProbabilitySection({ result }: SaleProbabilitySectionProps) {
  const { t } = useTranslation('records');

  const saleProbabilityRaw = result.summary?.saleProbability;
  if (saleProbabilityRaw === undefined || saleProbabilityRaw === null) return null;

  const saleProbPct = Math.round(saleProbabilityRaw * 100);
  const indicators = result.summary?.saleProbabilityIndicators;
  const confidenceLabel = confidenceLevelLabel(result.meta?.confidenceLevel, t);

  return (
    <AnalysisSection
      title={t('records.analysisPanel.saleProbability', 'Sale Probability')}
      summary={
        <span
          className={cn('font-mono text-xs font-semibold', scoreTextColor(saleProbPct))}
        >
          {saleProbPct}%
        </span>
      }
    >
      <div className="h-2 overflow-hidden rounded-full bg-bg-subtle">
        <div
          className={cn('h-full rounded-full', scoreBgColor(saleProbPct))}
          style={{ width: `${saleProbPct}%` }}
        />
      </div>

      {confidenceLabel !== null && (
        <div className="mt-2.5 flex items-center justify-between text-xs">
          <span className="text-fg-muted">
            {t('records.analysisPanel.confidenceLabel', 'Confidence')}
          </span>
          <span className="text-fg-secondary">{confidenceLabel}</span>
        </div>
      )}

      {/* WS7-T4: this number has never been checked against a closed deal — the
          product stores no outcome to check it against. Say so, here, where the
          transcript that produced it is on the same screen. */}
      <p className="mt-2.5 text-micro leading-snug text-fg-muted">
        {t(
          'records.analysisPanel.saleProbabilityDisclaimer',
          'AI estimate from the conversation alone. Not validated against the deal outcome.'
        )}
      </p>

      {indicators && indicators.length > 0 && (
        <div className="mt-2.5">
          <div className="mb-1 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
            {t('records.analysisPanel.keyFactors', 'Key Factors')}
          </div>
          <div className="flex flex-col gap-1">
            {indicators.map((ind, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs">
                <span
                  className={
                    ind.impact === 'positive'
                      ? 'text-success'
                      : ind.impact === 'negative'
                        ? 'text-danger'
                        : 'text-fg-muted'
                  }
                >
                  {ind.impact === 'positive'
                    ? '▲'
                    : ind.impact === 'negative'
                      ? '▼'
                      : '●'}
                </span>
                <span className="flex-1 text-fg-secondary">{ind.indicator}</span>
                {ind.confidence !== undefined && (
                  <span className="font-mono text-micro text-fg-faint">
                    {Math.round(ind.confidence * 100)}%
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AnalysisSection>
  );
}
